import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/GamePage.css';
import { useAuth } from '../context/AuthContext';
import { usePayment } from '../context/PaymentContext';
import Preloader from './Preloader';
import { FaChevronLeft } from 'react-icons/fa';

import { getApiUrl } from "../utils/config";
import { formatBalanceLocale } from "../utils/numberFormat";
const API_URL = getApiUrl();

const GamePage = () => {
    const { gameId } = useParams();
    const [searchParams] = useSearchParams();
    const isDemo = searchParams.get('demo') === 'true';
    const gameName = searchParams.get('name');

    const navigate = useNavigate();
    const [gameUrl, setGameUrl] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const sessionCreated = useRef(false);
    const [sessionId, setSessionId] = useState('');
    const { isAuth } = useAuth();
    const { openPaymentModal, getCurrentBalance, selectedCurrency } = usePayment();

    // Добавим панель управления для ПК версии
    const isMobile = window.innerWidth <= 768;

    const handleBack = async () => {
        await handleWindowClose();
    };

    const controlPanel = (
        <div className="control-panel">
            <div className="back-button-gamepage">
                <FaChevronLeft />
                <span onClick={handleBack}>Назад</span>
            </div>
            <span className="game-title">{gameName}</span>
        </div>
    );

    const [showPreloader, setShowPreloader] = useState(true);

    useEffect(() => {
        if (!isLoading) {
            const timer = setTimeout(() => setShowPreloader(false), 1000);
            return () => clearTimeout(timer);
        }
    }, [isLoading]);

    useEffect(() => {

        const launchGame = async () => {
            // Проверяем, не была ли уже создана сессия
            if (sessionCreated.current) {
                return;
            }

            try {
                setIsLoading(true);
                sessionCreated.current = true;
                let response;
                if (isAuth) {
                    response = await fetch(`${API_URL}/api/games/launch`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        credentials: 'include',
                        body: JSON.stringify({
                            gameId: gameId,
                            currency: 'RUB',
                            isDemo: isDemo
                        }),
                    });
                } else {
                    response = await fetch(`${API_URL}/api/games/demo-launch`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                            gameId: gameId
                        }),
                    });
                }

                if (!response.ok) {
                    throw new Error('Ошибка запуска игры');
                }

                const { token, server, session_id } = await response.json();

                setSessionId(session_id);
                // Формируем новый URL для загрузки игры одной строкой
                const gameUrl = `https://${server}/icasino2/load_game?operator=oncaspin.com&game=${encodeURIComponent(gameId)}${token ? `&session=${encodeURIComponent(token)}` : ''}&lobbyUrl=${encodeURIComponent(`${getApiUrl()}?close`)}&lang=RU`;
                setGameUrl(gameUrl);
            } catch (err) {
                console.error('Ошибка при запуске игры:', err);
                setError('Не удалось запустить игру. Пожалуйста, попробуйте позже.');
                sessionCreated.current = false;
            } finally {
                setIsLoading(false);
            }
        };

        launchGame();

        // Обработчик событий от игры
        const processEvent = async (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log('Событие от игры:', data);

                switch (data.event) {
                    case 'close':
                        navigate('/');

                        break;
                    case 'spin-begin':
                        console.log('Начало раунда:', { credit: data.credit, bet: data.bet });
                        break;
                    case 'spin-finish':
                        console.log('Конец раунда:', { credit: data.credit, win: data.win });
                        break;
                    case 'change-lines':
                        console.log('Изменение линий:', { credit: data.credit });
                        break;
                    case 'change-bet':
                        console.log('Изменение ставки:', { credit: data.credit });
                        break;
                    default:
                        console.log('Неизвестное событие:', data);
                }
            } catch (err) {
                console.error('Ошибка обработки события:', err);
            }
        };

        if (window.addEventListener) {
            window.addEventListener('message', processEvent, false);
        } else {
            window.attachEvent('onmessage', processEvent);
        }

        // Очистка при размонтировании
        return () => {
            if (window.removeEventListener) {
                window.removeEventListener('message', processEvent);
            } else {
                window.detachEvent('onmessage', processEvent);
            }

            // Закрываем сессию игры при выходе со страницы
            window.addEventListener('beforeunload', handleWindowClose);
            return () => {
                window.removeEventListener('beforeunload', handleWindowClose);
            };
        };
    }, [gameId, navigate, isDemo]);

    const handleWindowClose = async () => {
        console.log('Закрытие игры');
        if (sessionId) {
            try {
                await fetch(`${API_URL}/api/games/close/${sessionId}`, {
                    method: 'POST',
                    credentials: 'include'
                });
                console.log('Сессия успешно закрыта');
                navigate('/');
            } catch (err) {
                console.error('Ошибка при закрытии игры:', err);
            }
        }
    };

    // Функция для получения символа валюты
    const getCurrencySymbol = (currency) => {
        switch (currency) {
            case 'USD':
                return '$';
            case 'RUB':
                return '₽';
            case 'BRL':
                return 'R$';
            default:
                return '$';
        }
    };

    if (isLoading) {
        return <Preloader isLoading={isLoading} />;
    }

    if (error) {
        return <div className="game-error">{error}</div>;
    }

    return (
        <>
            {!isMobile && controlPanel}
            <div className="game-container">
                {gameUrl && (
                    <iframe
                        src={gameUrl}
                        title="Игра"
                        className="game-frame"
                        allow="fullscreen"
                    />
                )}
                {isMobile && (
                    <div className="mobile-control-panel">
                        <div className="back-button-gamepage">
                            <FaChevronLeft />
                            <span onClick={handleBack}>Назад</span>
                        </div>
                        <div className="right-section">
                            <span className="balance">{formatBalanceLocale(getCurrentBalance())}{getCurrencySymbol(selectedCurrency)}</span>
                            <button className="btn sign-up glow-blue" onClick={() => openPaymentModal({ type: "deposit" })}>Пополнить</button>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

export default GamePage; 